import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import BottomNav from '../components/BottomNav';
import LessonBannerAd from '../components/LessonBannerAd';
import { fetchGrammarTopics } from '../api/grammarApi';
import type { GrammarTopicRow } from '../api/grammarApi';
import '../screens/GrammarScreen.css';

const DEFAULT_TOPICS: { type: string; title_it: string; title_bn: string; title_en: string; icon: string }[] = [
  { type: 'verbs', title_it: 'Verbi', title_bn: 'ক্রিয়া', title_en: 'Verbs', icon: '⚡' },
  { type: 'verb-tenses', title_it: 'Tempi Verbali', title_bn: 'ক্রিয়া কাল', title_en: 'Verb Tenses', icon: '⏰' },
  { type: 'articles', title_it: 'Articoli', title_bn: 'আর্টিকেল', title_en: 'Articles', icon: '📝' },
  { type: 'pronouns', title_it: 'Pronomi', title_bn: 'সর্বনাম', title_en: 'Pronouns', icon: '👤' },
  { type: 'prepositions', title_it: 'Preposizioni', title_bn: 'অব্যয়', title_en: 'Prepositions', icon: '📍' },
  { type: 'adjectives', title_it: 'Aggettivi', title_bn: 'বিশেষণ', title_en: 'Adjectives', icon: '✨' },
  { type: 'questions', title_it: 'Domande', title_bn: 'প্রশ্ন', title_en: 'Questions', icon: '❓' },
  { type: 'negation', title_it: 'Negazione', title_bn: 'নিষেধ', title_en: 'Negation', icon: '🚫' },
];

// Varied colors for grammar topic cards (same style as WordsScreen)
const GRAMMAR_CARD_COLORS = [
  '#8b5cf6', '#22c55e', '#f59e0b', '#3b82f6', '#ec4899', '#06b6d4', '#84cc16', '#ef4444',
  '#6366f1', '#14b8a6', '#eab308', '#f97316', '#a855f7', '#10b981', '#0ea5e9', '#d946ef',
];

const GrammarScreen: React.FC = () => {
  const navigate = useNavigate();
  const { currentLanguage } = useLanguage();
  const [topicsFromApi, setTopicsFromApi] = useState<GrammarTopicRow[] | null>(null);

  useEffect(() => {
    fetchGrammarTopics().then((data) => {
      if (data && data.length > 0) setTopicsFromApi(data);
      else setTopicsFromApi(null);
    });
  }, []);

  const content = {
    bn: { title: 'ব্যাকরণ বুস্ট', back: 'ফিরে যান' },
    it: { title: 'Spinta Grammatica', back: 'Indietro' },
    en: { title: 'Grammar Boost', back: 'Back' },
  };

  const topicsToShow =
    topicsFromApi && topicsFromApi.length > 0
      ? topicsFromApi.map((t, i) => ({
          type: t.type,
          title_it: t.title_it,
          title_bn: t.title_bn,
          title_en: t.title_en,
          icon: DEFAULT_TOPICS.find((d) => d.type === t.type)?.icon || '📐',
          color: (t.color && t.color.trim()) ? t.color : GRAMMAR_CARD_COLORS[i % GRAMMAR_CARD_COLORS.length],
        }))
      : DEFAULT_TOPICS.map((t, i) => ({ ...t, color: GRAMMAR_CARD_COLORS[i % GRAMMAR_CARD_COLORS.length] }));

  const handleCardClick = (type: string) => {
    navigate(`/grammar/${type}`);
  };

  return (
    <div className="grammar-screen">
      <div className="screen-header">
        <button className="header-btn back-btn" onClick={() => navigate(-1)} aria-label="Go back">
          ←
        </button>
        <div className="header-center">
          <h1>{content[currentLanguage].title}</h1>
        </div>
        <button className="header-btn settings-btn" onClick={() => navigate('/settings')} aria-label="Open settings">
          ⋯
        </button>
      </div>

      <LessonBannerAd />

      <div className="featured-section">
        <div className="featured-cards">
          {topicsToShow.map((topic) => (
            <div
              key={topic.type}
              className="featured-card practice"
              onClick={() => handleCardClick(topic.type)}
              style={{
                backgroundColor: topic.color ? `${topic.color}22` : undefined,
                borderLeft: topic.color ? `4px solid ${topic.color}` : undefined,
              }}
            >
              <div className="card-header">
                <span className="card-title">
                  <span className="card-title-line">{topic.title_it}</span>
                  <span className="card-title-line">{topic.title_bn}</span>
                </span>
                <span className="heart-icon">♡</span>
              </div>
              <div className="card-icon">{topic.icon}</div>
            </div>
          ))}
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default GrammarScreen;
